import React, { useRef, useState, useEffect } from "react";

import { BarChartComponent } from "./bar-chart";
import type { BarChartProps } from "./bar-chart";

interface BarChartParentProps<DataType> extends BarChartProps<DataType> {
  minHeight?: number;
  maxHeight?: number;
}

export const BarChart = <DataType,>({ minHeight = 200, maxHeight, ...props }: BarChartParentProps<DataType>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const { width, height } = container.getBoundingClientRect();
      let finalHeight = Math.max(height, minHeight);
      if (maxHeight) finalHeight = Math.min(finalHeight, maxHeight);
      setDimensions({ 
        width, 
        height: finalHeight 
      });
    };

    // Initial measurement after layout
    updateDimensions();

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [minHeight, maxHeight]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        minHeight: `${minHeight}px`, 
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        height: maxHeight ? `${maxHeight}px` : '100%' 
      }}
    >
      {dimensions.width > 0 && (
        <BarChartComponent<DataType>
          width={dimensions.width}
          height={dimensions.height}
          {...props}
        />
      )}
    </div>
  );
};
